import { useTranslate } from "react-admin";

import {
  applyRescanRiskLevel,
  exportRiskCheckingRecords as exportRiskCheckingRecordsApi,
  getLockTime,
  getRescanResult,
  getRiskCheckingRecords,
  type ApplyWalletRiskLevelRequest,
  type ApplyWalletRiskLevelResponse,
  type GetLockTimeResponse,
  type GetRiskCheckingRecordsRequest,
  type GetRiskCheckingRecordsResponse,
  type MerkleScienceResponse,
  type RescanWalletRiskLevelRequest,
  type RescanWalletRiskLevelResponse,
  RiskCheckingRecord,
} from "../../../api/merchant";
import { useAlerting } from "../../../hooks";
import { appError } from "../../../utils/errorHandler";

// Backend error code for "calling rescan while still in cooldown". The
// axios interceptor's global error toast is suppressed only inside
// `useGetRescanResult` so we can show a localized message and let the
// dialog stay on the confirm step instead of erroring out.
const RESCAN_LOCK_RES_BAD_REQUEST = -20;

// Internal shape the dialog consumes. Kept camelCase so the dialog's
// existing field reads stay untouched.
export interface RescanResult {
  returned_risk_level: number;
  rescan_time: string;
  triggered_by: string;
  raw_response?: MerkleScienceResponse | null;
  outcome?: string;
  request_id?: string;
  // Backend's id for this rescan record — needed by the apply call. We
  // carry it through the meta so the dialog doesn't have to keep two
  // parallel sources of truth.
  rescan_id?: number;
  // Non-null when the rescan failed (provider unreachable, etc.). Surfaced
  // in the ProviderResponseDialog so a failed rescan isn't silent.
  error_reason?: string | null;
}

// Map the wire-shape response into the dialog's internal RescanResult.
// Keeping this in one place means a future field rename on either side
// touches one function, not three call sites.
const mapResponseToResult = (
  r: RescanWalletRiskLevelResponse,
): RescanResult => ({
  // Backend sends `response_risk_level`; dialog reads `returned_risk_level`.
  // Fall back to -2 (NoData) when the provider hasn't scored anything yet.
  returned_risk_level:
    typeof r.response_risk_level === "number" ? r.response_risk_level : -2,
  rescan_time: r.created_date,
  // `created_by` is the email of the operator who triggered the rescan.
  triggered_by: r.created_by,
  raw_response: r.merkle_science_response ?? null,
  // `status` is numeric; stringify so the dialog can compare/display freely.
  outcome: String(r.status),
  // The rescan record id serves double duty as a request-correlation token.
  request_id: String(r.wallet_risk_level_rescan_id),
  rescan_id: r.wallet_risk_level_rescan_id,
  error_reason: r.error_reason ?? null,
});

// Wire-shape envelope the backend returns. With `isSkipErrorHandling: true`,
// the axios interceptor hands back the full envelope (with `error` and
// `requestId`) instead of just `data.result`, so the caller can branch on
// `error.code` and decide between a localized toast and the global handler.
interface RescanApiEnvelope<T> {
  result?: T;
  error?: {
    code?: number;
    message?: string;
    data?: unknown | null;
    inner_error?: unknown | null;
  } | null;
  requestId?: string;
}

/**
 * Calls the "get rescan result" endpoint.
 *
 * The `isSkipErrorHandling: true` flag tells the axios interceptor to hand
 * back the full envelope (with `error` and `requestId`) instead of just
 * `data.result`, so we can branch on `error.code` here.
 *
 *   * `-20` ("called within the lock window") → show a localized toast,
 *     return `undefined`. The caller (dialog) stays on the confirm step.
 *   * any other error → hand off to the project's global handler so the
 *     user sees the standard translated toast. The caller receives
 *     `undefined` so it can stop the flow (e.g. keep the dialog on confirm).
 *   * success → return the mapped RescanResult.
 */
export const useGetRescanResult = () => {
  const translate = useTranslate();
  const t = (key: string) => translate(`walletManagement.${key}`);
  const { alerting } = useAlerting();

  const getRescan = async (
    request: RescanWalletRiskLevelRequest,
  ): Promise<RescanResult | undefined> => {
    const response = (await getRescanResult(request, {
      isSkipErrorHandling: true,
    } as any)) as RescanApiEnvelope<RescanWalletRiskLevelResponse> | undefined;

    if (!response) return undefined;

    // -20: backend says we're calling inside the lock window — the
    // precondition check we did before opening the dialog raced against
    // another operator's rescan. Show a specific localized message and
    // stay on the confirm step (the dialog's lock check will catch it on
    // the next open).
    if (response.error?.code === RESCAN_LOCK_RES_BAD_REQUEST) {
      alerting("warning", t("rescan_locked_toast"));
      return undefined;
    }

    // Any other error: hand off to the project's global handler so the
    // user sees the standard translated toast. The caller still receives
    // `undefined` so it can stop the flow (e.g. keep the dialog on confirm).
    if (response.error) {
      appError({
        code: response.error.code ?? 0,
        message: response.error.message ?? "",
        data: response.error.data,
        requestId: response.requestId ?? "",
      });
      return undefined;
    }

    return mapResponseToResult(response.result!);
  };

  return { getRescan };
};

/**
 * Calls the "apply rescan risk level" endpoint.
 * Lets the axios interceptor show its standard toast on any error — this
 * call is rare enough that a specialized branch isn't worth the wiring.
 */
export const useApplyRescanRiskLevel = () => {
  const translate = useTranslate();
  const t = (key: string) => translate(`walletManagement.${key}`);
  const { alerting } = useAlerting();

  const applyRescan = async (
    request: ApplyWalletRiskLevelRequest,
  ): Promise<boolean> => {
    const response: any = await applyRescanRiskLevel(request, {
      isSkipErrorHandling: true,
    });
    if (!response) return false;
    if (response.error?.code === -22) {
      alerting("warning", t("rescan_apply_expired"));
      return false;
    }
    if (response.error) {
      appError({
        ...response.error,
        requestId: response.requestId ?? "",
      });
      return false;
    }
    if (response.result?.result) {
      alerting("success", t("rescan_apply_succeeded"));
    }
    return response.result?.result ?? false;
  };

  return { applyRescan };
};

/**
 * Fetches the rescan cooldown duration. The returned value replaces the
 * compile-time `RESCAN_LOCK_TIME_MS` constant in the rescan dialog's
 * subcomponents — they all read `lock_time_ms` from meta with that constant
 * as a fallback.
 *
 * `isSkipErrorHandling: true` keeps the global error toast silent if the
 * backend is down — the dialog still opens at the confirm step with the
 * fallback lock duration.
 */
export const useGetLockTime = () => {
  const getLockTimeFor = async (): Promise<GetLockTimeResponse | undefined> => {
    return getLockTime({ isSkipErrorHandling: true } as any);
  };

  return { getLockTime: getLockTimeFor };
};

/**
 * Lists risk-checking records.
 *
 *   * Tab use case: regular filter + pagination.
 *   * Dialog use case: same filter + `pageSize: 1` to fetch only the most
 *     recent record for the wallet. The dialog then reads `rescan_time`
 *     from that record to drive its lock-step rendering.
 *
 * Wire shape isn't final yet — the row fields are loose. Will tighten once
 * the backend response is locked down.
 */
export const useGetRiskCheckingRecords = () => {
  const getRiskCheckingRecordsFor = async (
    request: GetRiskCheckingRecordsRequest,
  ): Promise<GetRiskCheckingRecordsResponse | undefined> => {
    return getRiskCheckingRecords(request);
  };

  return { getRiskCheckingRecords: getRiskCheckingRecordsFor };
};

/**
 * Export the risk-checking records list. Same request shape as the
 * get-all endpoint — the backend filters by the same fields. Used by the
 * Risk Checking Record tab's Export button (renders CSV/XLSX via the
 * project's `downloadFiles` helper).
 *
 * Returns the flat array of records (the backend wraps them in
 * `{ error, result, requestId }` and the axios interceptor unwraps to
 * `result`, which IS the array).
 */
export const useExportRiskCheckingRecords = () => {
  const fetchExport = async (
    request: GetRiskCheckingRecordsRequest,
    config?: any,
  ): Promise<RiskCheckingRecord[] | undefined> => {
    return exportRiskCheckingRecordsApi(request, config);
  };

  return { exportRiskCheckingRecords: fetchExport };
};
